/**
 * EternityFrame — The shared gold-on-black arch frame.
 *
 * Same frame for every icon. No gradients, no color.
 * Just the luminance of gold emerging from absolute darkness.
 * Byzantine gold leaf on a black ground.
 */

import { useId, useMemo } from 'react';

export interface EternityFrameProps {
  /** Inner content width in px */
  width: number;
  /** Height = width * heightRatio */
  heightRatio?: number;
  /** Arch shape: 0 = round, 1 = pointed */
  archPointedness?: number;
  /** Where the arch curve begins (fraction from top) */
  archStart?: number;
  /** Frame border thickness in px */
  frameThickness?: number;
  /** Gold color — flat, no gradients */
  goldColor?: string;
  /** Slightly brighter gold for inner highlight edge */
  highlightColor?: string;
  children?: React.ReactNode;
}

export function EternityFrame({
  width,
  heightRatio = 1.45,
  archPointedness = 0.22,
  archStart = 0.33,
  frameThickness = 14,
  goldColor = '#9B7A38',
  highlightColor = '#C5A258',
  children,
}: EternityFrameProps) {
  const id = useId().replace(/:/g, '');
  const clipId = `ef-clip-${id}`;

  const h = width * heightRatio;
  const t = frameThickness;
  const outerW = width + t * 2;
  const outerH = h + t;

  const innerPath = useMemo(
    () => buildArchPath(width, h, archPointedness, archStart, t, t),
    [width, h, archPointedness, archStart, t],
  );
  const outerArchStart = (h * archStart + t) / (h + 2 * t);
  const outerPath = useMemo(
    () => buildArchPath(outerW, outerH + t, archPointedness, outerArchStart, 0, 0),
    [outerW, outerH, archPointedness, outerArchStart],
  );
  const reversePath = useMemo(
    () => reverseArchPath(width, h, archPointedness, archStart, t, t),
    [width, h, archPointedness, archStart, t],
  );

  // Frame fill = outer minus inner (evenodd)
  const framePath = `${outerPath} ${reversePath}`;

  return (
    <div
      style={{
        position: 'relative',
        width: outerW,
        height: outerH,
        overflow: 'visible',
      }}
    >
      {/* SVG frame overlay */}
      <svg
        viewBox={`0 0 ${outerW} ${outerH}`}
        width={outerW}
        height={outerH}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 5,
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        <defs>
          <clipPath id={clipId}>
            <path d={innerPath} />
          </clipPath>
        </defs>

        {/* Frame body — flat gold, no gradients */}
        <path d={framePath} fillRule="evenodd" fill={goldColor} />

        {/* Outer edge highlight — single bright line */}
        <path d={outerPath} fill="none" stroke={highlightColor} strokeWidth={1.2} />

        {/* Inner edge — dark line for depth */}
        <path d={innerPath} fill="none" stroke="rgba(40, 30, 10, 0.6)" strokeWidth={1} />

        {/* Inner highlight — slight gold catch */}
        <path
          d={innerPath}
          fill="none"
          stroke="rgba(197, 162, 88, 0.2)"
          strokeWidth={0.5}
          transform="translate(-0.5, -0.5)"
        />
      </svg>

      {/* Content area — clipped to arch shape */}
      <div
        style={{
          position: 'absolute',
          left: t,
          top: t,
          width,
          height: h,
          clipPath: `url(#${clipId})`,
          WebkitClipPath: `url(#${clipId})`,
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── Path builders ──

function buildArchPath(
  w: number, h: number,
  pointedness: number, archStart: number,
  offsetX: number, offsetY: number,
): string {
  const archY = h * archStart + offsetY;
  const top = offsetY;
  const bottom = h + offsetY;
  const left = offsetX;
  const right = w + offsetX;
  const midX = (left + right) / 2;
  const cpx = w * (0.15 + pointedness * 0.2);
  const cpy = top + h * (0.02 + pointedness * 0.08);

  return [
    `M ${left} ${bottom}`,
    `L ${left} ${archY}`,
    `C ${left} ${offsetY + (archY - offsetY) * 0.4},`,
    `  ${left + cpx} ${cpy},`,
    `  ${midX} ${top}`,
    `C ${right - cpx} ${cpy},`,
    `  ${right} ${offsetY + (archY - offsetY) * 0.4},`,
    `  ${right} ${archY}`,
    `L ${right} ${bottom}`,
    'Z',
  ].join(' ');
}

function reverseArchPath(
  w: number, h: number,
  pointedness: number, archStart: number,
  offsetX: number, offsetY: number,
): string {
  const archY = h * archStart + offsetY;
  const top = offsetY;
  const bottom = h + offsetY;
  const left = offsetX;
  const right = w + offsetX;
  const midX = (left + right) / 2;
  const cpx = w * (0.15 + pointedness * 0.2);
  const cpy = top + h * (0.02 + pointedness * 0.08);

  return [
    `M ${right} ${bottom}`,
    `L ${right} ${archY}`,
    `C ${right} ${offsetY + (archY - offsetY) * 0.4},`,
    `  ${right - cpx} ${cpy},`,
    `  ${midX} ${top}`,
    `C ${left + cpx} ${cpy},`,
    `  ${left} ${offsetY + (archY - offsetY) * 0.4},`,
    `  ${left} ${archY}`,
    `L ${left} ${bottom}`,
    'Z',
  ].join(' ');
}

export default EternityFrame;
